<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import TicketCategoriesAPI from 'dashboard/api/ticketCategories';

import AgentLayout from 'dashboard/components-next/aiAgents/agent/AgentLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useBrain } from './useBrain';

// Live section — ticket taxonomy agent_policy (D14), shown under the
// "shared across all AI agents" banner (D35 resolved tension #1): one-place
// editing stays in the taxonomy settings, the banner prevents silent
// cross-agent surprise.
const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { brain } = useBrain();

const brains = useMapGetter('aiAgentBrains/getRecords');

const categories = ref([]);
const subcategoriesByCategory = ref({});
const expandedPolicies = ref({});
const isFetchingTaxonomy = ref(false);

const otherAgentNames = computed(() =>
  brains.value
    .filter(record => record.id !== brain.value.id)
    .map(record => record.display_name)
    .join(', ')
);

const fetchTaxonomy = async () => {
  isFetchingTaxonomy.value = true;
  try {
    const response = await TicketCategoriesAPI.get();
    categories.value = response.data.payload || response.data || [];
    await Promise.all(
      categories.value.map(async category => {
        const subResponse = await TicketCategoriesAPI.getSubcategories(
          category.id
        );
        subcategoriesByCategory.value[category.id] =
          subResponse.data.payload || subResponse.data || [];
      })
    );
  } catch (error) {
    useAlert(t('AI_AGENTS.TICKETS.LOAD_ERROR'));
  } finally {
    isFetchingTaxonomy.value = false;
  }
};

onMounted(() => {
  store.dispatch('aiAgentBrains/get');
  fetchTaxonomy();
});

const togglePolicy = subcategoryId => {
  expandedPolicies.value[subcategoryId] =
    !expandedPolicies.value[subcategoryId];
};

const hasPolicy = subcategory =>
  subcategory.agent_policy && Object.keys(subcategory.agent_policy).length > 0;

const formatPolicy = subcategory =>
  JSON.stringify(subcategory.agent_policy, null, 2);
</script>

<template>
  <AgentLayout v-if="brain.id" :brain="brain">
    <div class="flex flex-col gap-4 max-w-3xl">
      <h2 class="text-lg font-medium text-n-slate-12">
        {{ t('AI_AGENTS.TICKETS.HEADER') }}
      </h2>

      <div
        class="flex flex-col gap-1 px-3 py-2 text-sm rounded-lg bg-n-amber-3 text-n-amber-12"
      >
        <span class="font-semibold">
          {{ t('AI_AGENTS.TICKETS.BANNER.TITLE') }}
        </span>
        <span v-if="otherAgentNames">
          {{
            t('AI_AGENTS.TICKETS.BANNER.AGENTS', { agents: otherAgentNames })
          }}
        </span>
        <span v-else>{{ t('AI_AGENTS.TICKETS.BANNER.ONLY_AGENT') }}</span>
        <RouterLink
          :to="{
            name: 'ticket_categories_list',
            params: { accountId: route.params.accountId },
          }"
          class="text-n-blue-11 hover:underline w-fit"
        >
          {{ t('AI_AGENTS.TICKETS.BANNER.LINK') }}
        </RouterLink>
      </div>

      <div
        v-if="isFetchingTaxonomy"
        class="flex justify-center py-6 text-n-slate-11"
      >
        <Spinner />
      </div>
      <p v-else-if="!categories.length" class="text-sm text-n-slate-11">
        {{ t('AI_AGENTS.TICKETS.EMPTY') }}
      </p>
      <template v-else>
        <div
          v-for="category in categories"
          :key="category.id"
          class="flex flex-col gap-2"
        >
          <h3 class="text-base font-medium text-n-slate-12">
            {{ category.name }}
          </h3>
          <div
            v-for="subcategory in subcategoriesByCategory[category.id] || []"
            :key="subcategory.id"
            class="flex flex-col gap-1 px-3 py-2 rounded-lg outline outline-1 outline-n-weak"
          >
            <div class="flex items-center justify-between gap-2">
              <span class="text-sm font-medium text-n-slate-12">
                {{ subcategory.name }}
              </span>
              <button
                v-if="hasPolicy(subcategory)"
                class="text-xs text-n-blue-11 hover:underline"
                @click="togglePolicy(subcategory.id)"
              >
                {{
                  expandedPolicies[subcategory.id]
                    ? t('AI_AGENTS.TICKETS.HIDE_POLICY')
                    : t('AI_AGENTS.TICKETS.SHOW_POLICY')
                }}
              </button>
              <span v-else class="text-xs text-n-slate-10">
                {{ t('AI_AGENTS.TICKETS.NO_POLICY') }}
              </span>
            </div>
            <pre
              v-if="expandedPolicies[subcategory.id]"
              class="px-3 py-2 overflow-x-auto text-xs rounded-lg bg-n-solid-3 text-n-slate-12"
              >{{ formatPolicy(subcategory) }}</pre
            >
          </div>
        </div>
      </template>
    </div>
  </AgentLayout>
  <div
    v-else
    class="flex items-center justify-center w-full h-full text-n-slate-11"
  >
    <Spinner />
  </div>
</template>
